import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, Store, Share2, Trash2, Download, LogOut, RefreshCw, Trophy, MapPin, Mail, Gift } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return String(detail);
};

const toCsv = (rows, headers) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.map(esc).join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
};

const downloadCsv = (name, rows, headers) => {
  const blob = new Blob([toCsv(rows, headers)], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const cardCls = 'bg-white border border-black/10 shadow-sm rounded-3xl';
const iconBadge = 'w-9 h-9 rounded-xl bg-[#f15a24]/10 flex items-center justify-center shrink-0';
const ghostBtn =
  'flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-xs text-[#6e6a63] hover:text-[#f15a24] hover:border-[#f15a24]/50 transition-colors duration-300';

const Admin = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [tab, setTab] = useState('waitlist');
  const [cityFilter, setCityFilter] = useState('all');
  const [rewards, setRewards] = useState({ '5': '', '10': '', '25': '' });
  const [savingReward, setSavingReward] = useState(false);

  const authed = { credentials: 'include' };

  const loadData = useCallback(async () => {
    try {
      const [s, w, r, st] = await Promise.all([
        fetch(`${API}/admin/stats`, authed).then((x) => x.json()),
        fetch(`${API}/admin/waitlist`, authed).then((x) => x.json()),
        fetch(`${API}/admin/restaurants`, authed).then((x) => x.json()),
        fetch(`${API}/admin/settings`, authed).then((x) => x.json()),
      ]);
      setStats(s);
      setWaitlist(Array.isArray(w) ? w : []);
      setRestaurants(Array.isArray(r) ? r : []);
      const t = st?.milestone_rewards || {};
      setRewards({ '5': t['5'] || '', '10': t['10'] || '', '25': t['25'] || '' });
    } catch {
      toast.error('Could not load dashboard data.');
    }
  }, []);

  useEffect(() => {
    fetch(`${API}/auth/me`, authed)
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => setUser(u || false))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(fmtError(data.detail));
      setUser(data);
      toast.success('Welcome back.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(false);
  };

  const removeEntry = async (kind, id) => {
    const res = await fetch(`${API}/admin/${kind}/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) {
      toast.success('Entry removed.');
      loadData();
    } else {
      toast.error('Delete failed.');
    }
  };

  const saveReward = async () => {
    setSavingReward(true);
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ milestone_rewards: rewards }),
      });
      if (res.ok) {
        toast.success('Milestone rewards saved.');
      } else {
        toast.error('Could not save rewards.');
      }
    } finally {
      setSavingReward(false);
    }
  };

  if (user === null) {
    return <div className="min-h-screen bg-[#faf9f6]" />;
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className={`w-full max-w-sm ${cardCls} p-8`}
        >
          <Logo className="h-6" testId="admin-logo" />
          <h1 className="mt-6 font-display font-medium text-2xl text-[#171512]">Admin sign in</h1>
          <form onSubmit={login} className="mt-6 space-y-4" data-testid="admin-login-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@dineora.ca"
              data-testid="admin-email-input"
              className="w-full bg-[#faf9f6] border border-black/10 rounded-2xl px-4 py-3.5 text-sm text-[#171512] placeholder-[#97917f] outline-none focus:border-[#f15a24]/60 transition-colors duration-300"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="admin-password-input"
              className="w-full bg-[#faf9f6] border border-black/10 rounded-2xl px-4 py-3.5 text-sm text-[#171512] placeholder-[#97917f] outline-none focus:border-[#f15a24]/60 transition-colors duration-300"
            />
            {error && (
              <p className="text-xs text-red-500" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-btn"
              className="w-full rounded-full bg-[#171512] hover:bg-black text-white font-semibold text-sm py-3.5 transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const cards = [
    { label: 'On the waitlist', value: stats?.waitlist_total ?? '—', Icon: Users, testId: 'stat-waitlist' },
    { label: 'Restaurants', value: stats?.restaurants_total ?? '—', Icon: Store, testId: 'stat-restaurants' },
    { label: 'Joined via referral', value: stats?.referred_total ?? '—', Icon: Share2, testId: 'stat-referred' },
  ];

  const leaders = [...waitlist]
    .filter((w) => w.referrals > 0)
    .sort((a, b) => b.referrals - a.referrals || a.position - b.position)
    .slice(0, 5);

  const cityMap = {};
  waitlist.forEach((w) => {
    const key = w.city || 'Not set';
    cityMap[key] = cityMap[key] || { diners: 0, restaurants: 0 };
    cityMap[key].diners += 1;
  });
  restaurants.forEach((r) => {
    const key = r.city || 'Not set';
    cityMap[key] = cityMap[key] || { diners: 0, restaurants: 0 };
    cityMap[key].restaurants += 1;
  });
  const cityRows = Object.entries(cityMap)
    .map(([city, v]) => ({ city, ...v }))
    .sort((a, b) => b.diners - a.diners || b.restaurants - a.restaurants)
    .slice(0, 8);
  const maxDiners = Math.max(1, ...cityRows.map((c) => c.diners));

  const cityOptions = [...new Set(waitlist.map((w) => w.city || 'Not set'))].sort();
  const visibleWaitlist =
    cityFilter === 'all' ? waitlist : waitlist.filter((w) => (w.city || 'Not set') === cityFilter);

  return (
    <div className="min-h-screen bg-[#faf9f6] text-[#171512]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-black/10 px-6 sm:px-10 py-5 flex items-center justify-between">
        <Logo className="h-6" testId="admin-header-logo" />
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            data-testid="admin-refresh-btn"
            className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center text-[#6e6a63] hover:text-[#f15a24] hover:border-[#f15a24]/50 transition-colors duration-300"
            aria-label="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={async () => {
              const res = await fetch(`${API}/admin/digest/send`, { method: 'POST', credentials: 'include' });
              if (res.ok) {
                const d = await res.json();
                toast.success(`Digest emailed to ${d.sent_to}`);
              } else {
                toast.error('Digest failed to send.');
              }
            }}
            data-testid="admin-send-digest-btn"
            className={ghostBtn}
          >
            <Mail size={13} /> Email digest
          </button>
          <button onClick={logout} data-testid="admin-logout-btn" className={ghostBtn}>
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cards.map(({ label, value, Icon, testId }) => (
            <div key={label} className={`${cardCls} p-6`} data-testid={testId}>
              <div className="flex items-center gap-3">
                <span className={iconBadge}>
                  <Icon size={16} className="text-[#f15a24]" aria-hidden="true" />
                </span>
                <p className="text-xs text-[#6e6a63] uppercase tracking-widest">{label}</p>
              </div>
              <p className="mt-4 font-display text-4xl text-[#171512]">{value}</p>
            </div>
          ))}
        </div>

        {leaders.length > 0 && (
          <div className={`mt-6 ${cardCls} p-6`} data-testid="admin-leaderboard">
            <div className="flex items-center gap-3 mb-2">
              <span className={iconBadge}>
                <Trophy size={16} className="text-[#f15a24]" aria-hidden="true" />
              </span>
              <p className="text-xs text-[#6e6a63] uppercase tracking-widest">Top inviters — reward them at launch</p>
            </div>
            <div className="divide-y divide-black/5">
              {leaders.map((w, i) => (
                <div key={w.id} className="flex items-center justify-between py-3" data-testid={`leaderboard-row-${i + 1}`}>
                  <div className="flex items-center gap-4">
                    <span className="font-display text-xl text-[#f15a24] w-7">{i + 1}</span>
                    <span className="text-sm text-[#171512]">{w.email}</span>
                  </div>
                  <span className="text-xs text-[#6e6a63]">{w.referrals} referral{w.referrals === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {cityRows.length > 0 && (
          <div className={`mt-6 ${cardCls} p-6`} data-testid="admin-city-chart">
            <div className="flex items-center gap-3 mb-4">
              <span className={iconBadge}>
                <MapPin size={16} className="text-[#f15a24]" aria-hidden="true" />
              </span>
              <p className="text-xs text-[#6e6a63] uppercase tracking-widest">Demand by city — pick your launch market</p>
            </div>
            <div className="space-y-3">
              {cityRows.map((c) => (
                <div
                  key={c.city}
                  className="flex items-center gap-4"
                  data-testid={`city-row-${c.city.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <span className="w-24 sm:w-28 shrink-0 text-sm text-[#171512] truncate">{c.city}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-black/5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(c.diners / maxDiners) * 100}%` }}
                      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full rounded-full bg-[#f15a24]"
                    />
                  </div>
                  <span className="w-32 shrink-0 text-right text-xs text-[#6e6a63]">
                    {c.diners} diner{c.diners === 1 ? '' : 's'}
                    {c.restaurants > 0 ? ` · ${c.restaurants} kitchen${c.restaurants === 1 ? '' : 's'}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`mt-6 ${cardCls} p-6`} data-testid="milestone-reward-card">
          <div className="flex items-center gap-3 mb-4">
            <span className={iconBadge}>
              <Gift size={16} className="text-[#f15a24]" aria-hidden="true" />
            </span>
            <p className="text-xs text-[#6e6a63] uppercase tracking-widest">Tiered milestone rewards — emailed at each referral goal</p>
          </div>
          <div className="space-y-3">
            {['5', '10', '25'].map((tier) => (
              <div key={tier} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <span className="shrink-0 sm:w-28 text-[11px] font-semibold uppercase tracking-wider text-[#f15a24] bg-[#f15a24]/10 rounded-full px-3 py-1.5 text-center">
                  {tier} referrals
                </span>
                <input
                  value={rewards[tier]}
                  onChange={(e) => setRewards({ ...rewards, [tier]: e.target.value })}
                  placeholder={
                    tier === '5'
                      ? 'e.g. Free delivery for a month'
                      : tier === '10'
                        ? 'e.g. $10 launch credit'
                        : 'e.g. A free lunch on us'
                  }
                  data-testid={`milestone-reward-input-${tier}`}
                  className="flex-1 bg-[#faf9f6] border border-black/10 rounded-full px-5 py-3 text-sm text-[#171512] placeholder-[#97917f] outline-none focus:border-[#f15a24]/60 transition-colors duration-300"
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              onClick={saveReward}
              disabled={savingReward}
              data-testid="milestone-reward-save-btn"
              className="rounded-full bg-[#171512] hover:bg-black text-white text-xs font-semibold px-6 py-3 transition-colors duration-300 disabled:opacity-60"
            >
              {savingReward ? 'Saving…' : 'Save rewards'}
            </button>
            <p className="text-[11px] text-[#97917f]">Leave a tier empty and that milestone email goes without a reward box.</p>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex gap-2 bg-white border border-black/10 rounded-full p-1">
            {['waitlist', 'restaurants'].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`admin-tab-${t}`}
                className={`rounded-full px-5 py-2 text-xs font-semibold capitalize transition-colors duration-300 ${
                  tab === t ? 'bg-[#171512] text-white' : 'text-[#6e6a63] hover:text-[#171512]'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <button
            onClick={() =>
              tab === 'waitlist'
                ? downloadCsv('dineora-waitlist.csv', waitlist, ['position', 'email', 'city', 'referral_code', 'referrals', 'referred_by', 'created_at'])
                : downloadCsv('dineora-restaurants.csv', restaurants, ['restaurant_name', 'email', 'city', 'service', 'created_at'])
            }
            data-testid="admin-export-csv-btn"
            className={`${ghostBtn} px-5 py-2.5`}
          >
            <Download size={13} /> Export CSV
          </button>
        </div>

        {tab === 'waitlist' && cityOptions.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="city-filter-bar">
            {['all', ...cityOptions].map((c) => (
              <button
                key={c}
                onClick={() => setCityFilter(c)}
                data-testid={`city-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                  cityFilter === c
                    ? 'bg-[#171512] text-white'
                    : 'bg-white border border-black/10 text-[#6e6a63] hover:text-[#171512] hover:border-black/25'
                }`}
              >
                {c === 'all' ? 'All cities' : c}
              </button>
            ))}
          </div>
        )}

        <div className={`mt-4 ${cardCls} overflow-hidden`}>
          {tab === 'waitlist' ? (
            <div className="overflow-x-auto" data-testid="waitlist-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-[#71716b] border-b border-black/10">
                    <th className="px-6 py-4">#</th>
                    <th className="px-6 py-4">Email</th>
                    <th className="px-6 py-4">City</th>
                    <th className="px-6 py-4">Invite code</th>
                    <th className="px-6 py-4">Referrals</th>
                    <th className="px-6 py-4">Joined</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {visibleWaitlist.map((w) => (
                    <tr key={w.id} className="border-b border-black/5 last:border-0 hover:bg-black/[0.02] transition-colors duration-200">
                      <td className="px-6 py-4 font-display text-lg text-[#f15a24]">{w.position}</td>
                      <td className="px-6 py-4 text-[#171512]">{w.email}</td>
                      <td className="px-6 py-4 text-[#6e6a63]">{w.city || '—'}</td>
                      <td className="px-6 py-4 font-mono2 text-xs text-[#6e6a63]">{w.referral_code}</td>
                      <td className="px-6 py-4 text-[#6e6a63]">{w.referrals}</td>
                      <td className="px-6 py-4 text-[#71716b] text-xs">{fmtDate(w.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => removeEntry('waitlist', w.id)}
                          data-testid={`waitlist-delete-${w.id}`}
                          className="text-[#b7b1a6] hover:text-red-500 transition-colors duration-200"
                          aria-label={`Remove ${w.email}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {visibleWaitlist.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-10 text-center text-[#71716b] text-sm">No signups for this city yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="restaurants-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-[#71716b] border-b border-black/10">
                    <th className="px-6 py-4">Restaurant</th>
                    <th className="px-6 py-4">Email</th>
                    <th className="px-6 py-4">City</th>
                    <th className="px-6 py-4">Service</th>
                    <th className="px-6 py-4">Registered</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {restaurants.map((r) => (
                    <tr key={r.id} className="border-b border-black/5 last:border-0 hover:bg-black/[0.02] transition-colors duration-200">
                      <td className="px-6 py-4 text-[#171512]">{r.restaurant_name}</td>
                      <td className="px-6 py-4 text-[#6e6a63]">{r.email}</td>
                      <td className="px-6 py-4 text-[#6e6a63]">{r.city || '—'}</td>
                      <td className="px-6 py-4"><span className="text-[11px] uppercase tracking-wider text-[#f15a24] bg-[#f15a24]/10 rounded-full px-3 py-1">{r.service}</span></td>
                      <td className="px-6 py-4 text-[#71716b] text-xs">{fmtDate(r.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => removeEntry('restaurants', r.id)}
                          data-testid={`restaurant-delete-${r.id}`}
                          className="text-[#b7b1a6] hover:text-red-500 transition-colors duration-200"
                          aria-label={`Remove ${r.restaurant_name}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {restaurants.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-10 text-center text-[#71716b] text-sm">No restaurants yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
